import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { useTranslate } from '@ds/comp-private'
import { InputDescription } from '../../../components/InputDescription'
import { useIsInk, useThemeStyles } from '../../../theming'
import styles from './styles'

export interface InputCountProps {
  /**
   * The current length of the string being considered.
   */
  currentLength: number
  /**
   * A React ref to assign to the HTML node representing the InputCount element.
   */
  inputRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement>
  /**
   * Identifier for automated testing.
   */
  'data-qa'?: string
  /**
   * Identifier for automated testing.
   */
  id: string
  /**
   *  The maximum valid length of the string being considered.
   */
  maxLength: number
}

let delay: null | number = null

export function InputCount({
  currentLength,
  'data-qa': dataQa,
  id,
  inputRef,
  maxLength,
}: InputCountProps) {
  const translate = useTranslate()
  const isInk = useIsInk()

  const charactersOverLimit = Math.max(0, currentLength - maxLength)
  const overCharacterLimit = currentLength > maxLength

  const [placeholder, setPlaceholder] = useState(false)

  const sx = useThemeStyles(styles, { overCharacterLimit })

  const inputCountValueOlive = charactersOverLimit || maxLength - currentLength

  const [announcedInputCountValueOlive, setAnnouncedInputCountValueOlive] =
    useState(!isInk ? inputCountValueOlive : '')

  const [announcedCharactersOverTheLimit, setAnnouncedCharactersOverTheLimit] =
    useState(charactersOverLimit)

  const [announcedInputCountLabel, setAnnouncedInputCountLabel] = useState(
    overCharacterLimit
      ? translate('OLIVE:OVER_CHAR_LIMIT_SUMMARY', {
          CURRENT_LENGTH: currentLength,
          MAX_LENGTH: maxLength,
        })
      : translate('OLIVE:CHAR_COUNT', {
          CURRENT_LENGTH: currentLength,
          MAX_LENGTH: maxLength,
        }),
  )

  const currentCharsRef = useRef(currentLength)
  const previousCharsRef = useRef(currentLength)
  const togglePlaceholder = useRef(false)

  currentCharsRef.current = currentLength

  const inputDescriptionType = overCharacterLimit ? 'error' : 'helper'

  useEffect(() => {
    const currentInputRef = inputRef?.current

    function updateCountdownAnnounced() {
      const announcedCharsOverLimit = Math.max(
        0,
        currentCharsRef.current - maxLength,
      )

      if (previousCharsRef.current === currentCharsRef.current) {
        togglePlaceholder.current = !togglePlaceholder.current
        setPlaceholder(togglePlaceholder.current)
      }

      setAnnouncedInputCountValueOlive(
        !isInk
          ? announcedCharsOverLimit || maxLength - currentCharsRef.current
          : '',
      )
      setAnnouncedCharactersOverTheLimit(announcedCharsOverLimit)

      setAnnouncedInputCountLabel(
        currentCharsRef.current > maxLength
          ? translate('OLIVE:OVER_CHAR_LIMIT_SUMMARY', {
              CURRENT_LENGTH: currentCharsRef.current,
              MAX_LENGTH: maxLength,
            })
          : translate('OLIVE:CHAR_COUNT', {
              CURRENT_LENGTH: currentCharsRef.current,
              MAX_LENGTH: maxLength,
            }),
      )

      previousCharsRef.current = currentCharsRef.current
    }

    function focusInputEvent() {
      currentInputRef?.dispatchEvent(new Event('input'))
    }

    function inputEvent() {
      if (delay) {
        window.clearTimeout(delay)
      }
      delay = window.setTimeout(updateCountdownAnnounced, 1000)
    }

    currentInputRef?.addEventListener('input', inputEvent, false)
    currentInputRef?.addEventListener('focus', focusInputEvent, false)
    return () => {
      if (delay) {
        window.clearTimeout(delay)
      }
      currentInputRef?.removeEventListener('input', inputEvent, false)
      currentInputRef?.removeEventListener('focus', focusInputEvent, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxLength])

  const inputCountLabel = overCharacterLimit ? (
    <span data-qa={dataQa && `${dataQa}-over-limit`} style={sx.default.text}>
      {isInk
        ? translate('OLIVE:OVER_CHAR_LIMIT')
        : translate('OLIVE:CHARS_OVER_LIMIT')}
    </span>
  ) : (
    <span data-qa={dataQa && `${dataQa}-remaining`}>
      {!isInk ? translate('OLIVE:CHARS_REMAINING') : ''}
    </span>
  )

  const oliveAnnouncedInputCountLabel = announcedCharactersOverTheLimit
    ? translate('OLIVE:CHARS_OVER_LIMIT')
    : translate('OLIVE:CHARS_REMAINING')

  const characterCountStyles = [sx.default.text, sx.default.count]

  const inputCountText = (
    <span
      css={sx.default.wrap}
      data-qa={dataQa && `${dataQa}-text`}
      aria-hidden
    >
      {inputCountLabel}{' '}
      <span data-qa={dataQa && `${dataQa}-value`} css={characterCountStyles}>
        {isInk ? `${currentLength}/${maxLength}` : `${inputCountValueOlive} `}
      </span>
    </span>
  )

  const hiddenTextValue1Line = `${
    placeholder ? '\u200b' : '' // u200b = zero width space. Added and removed so chars remaining is announced.
  }${isInk ? announcedInputCountLabel : oliveAnnouncedInputCountLabel} ${announcedInputCountValueOlive}`

  return (
    <>
      <InputDescription id={id} kind={inputDescriptionType} data-qa={dataQa}>
        {inputCountText}
      </InputDescription>

      <span
        role="status"
        css={sx.hidden}
        data-qa={dataQa && `${dataQa}-status-text`}
      >
        {hiddenTextValue1Line}
      </span>
    </>
  )
}

InputCount.propTypes = {
  currentLength: PropTypes.number.isRequired,
  'data-qa': PropTypes.string,
  id: PropTypes.string.isRequired,
  maxLength: PropTypes.number.isRequired,
}

InputCount.displayName = 'InputCount'
